<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap ma-0 mb-2 pa-0>
      <v-flex pt-0>
        <v-layout>
          <template>
            <h2>
              <span class="grey--text">Standalone</span> Refund
            </h2>
            <div class="pa-1 pl-2">
              <div class="subheading primary--text" v-if="selectedTerminal.name">{{ selectedTerminal.name }}</div>
              <div class="body-2 grey--text" v-if="selectedTerminal.code">{{ selectedTerminal.code }}</div>
            </div>
            <Lightbulb medium>
              <template v-slot:message>
                <span><strong>Use With Caution:</strong>This will allow for direct refund to a card without verifying a previous transaction.</span>
              </template>
            </Lightbulb>
          </template>
        </v-layout>
      </v-flex>
    </v-layout>

    <AccountTerminalDebug :terminal="selectedTerminal"></AccountTerminalDebug>

    <v-layout row wrap>
      <v-flex pt-0>
        <v-alert outline v-show="!accountCanRefund">
          Account does not have permissions to perform a standalone refund.
        </v-alert>
      </v-flex>
    </v-layout>

    <v-form ref="form1" v-model="isFormValid" @submit.prevent>
      <v-layout align-center row wrap>
        <v-flex xs12 sm4>
          <TerminalSelect
            ref="terminal"
            v-model="selectedTerminal"
            :accountId="selectedToken.account_id"
            :paymentType="paymentType"
            showDetails
          ></TerminalSelect>
        </v-flex>

        <v-flex xs12 sm4>
          <AmountThreshold
            v-model="fields.amount"
            required
            :error-messages="fields.amount ? '' : 'Required'"
          ></AmountThreshold>
        </v-flex>

        <!-- Override is available to superusers and account admins. The API scopes account admins to their accounts. -->
        <v-flex xs12 sm4 v-if="!accountCanRefund && (hasPermission('superuser') || isAccountAdmin())">
          <v-checkbox
            label="Temporarily Override Permissions Validation"
            v-model="fields.override"
            color="primary"
            box
            hide-details
            class="ma-0"
          ></v-checkbox>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="paymentType === 'card'">
        <v-flex xs12 sm4>
          <v-text-field
            label="Card Holder Name"
            v-model="fields.card_name"
            append-icon="fa-user"
            :rules="nameRules"
            box
            :disabled="selectedToken.id ? true : false"
            hide-details
            required
            :error-messages="fields.card_name ? '' : 'Required'"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            label="Card Number"
            v-model="fields.card_number"
            :append-icon="(!cardType || cardType==='unknown') ? 'fa-credit-card' : ''"
            :mask="cardMask"
            :rules="selectedToken.id ? [] : cardRules"
            box
            :disabled="selectedToken.id ? true : false"
            hide-details
            required
            :error-messages="fields.card_number ? '' : 'Required'"
          >
            <img slot="append" :src="'/static/cards/' + cardType + '.svg'" height="24px" v-if="cardType && cardType!=='unknown'" />
          </v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            label="Expiration"
            type="tel"
            v-model="fields.card_expiration"
            append-icon="fa-calendar"
            mask="## / ##"
            :rules="expirationRules"
            box
            hide-details
            :disabled="selectedToken.id ? true : false"
            required
            :error-messages="fields.card_expiration ? '' : 'Required'"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            label="CVV"
            type="test"
            v-model="fields.card_cvv"
            :mask="cvvMask"
            :rules="cvvRules"
            box
            :disabled="selectedToken.id ? true : false"
            hide-details
            :required="!selectedToken"
            :error-messages="fields.card_cvv ? '' : 'Required'"
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="paymentType === 'ach'">
        <v-flex xs12 sm4>
          <v-text-field
            label="Account Holder Name"
            v-model="fields.card_name"
            append-icon="fa-user"
            :rules="nameRules"
            box
            :disabled="selectedToken.id ? true : false"
            hide-details
            required
            :error-messages="fields.card_name ? '' : 'Required'"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4 pb-0>
          <v-text-field
            label="Routing Number"
            v-model="fields.ach_routing_number"
            append-icon="fa-exchange"
            :rules="routingRules"
            mask="#########"
            box
            :disabled="selectedToken.id ? true : false"
            hide-details
            required
            :error-messages="fields.ach_routing_number ? '' : 'Required'"
          ></v-text-field>
          <AchFindInfoModal></AchFindInfoModal>
        </v-flex>
        <v-flex xs6 sm4 pb-0>
          <v-text-field
            label="Account Number"
            v-model="fields.ach_account_number"
            append-icon="fa-institution"
            :rules="accountRules"
            box
            :disabled="selectedToken.id ? true : false"
            hide-details
            required
            :error-messages="fields.ach_account_number ? '' : 'Required'"
          ></v-text-field>
          <AchFindInfoModal></AchFindInfoModal>
        </v-flex>
        <v-flex xs6 sm4>
          <v-select
            label="Type"
            v-model="fields.ach_type"
            :items="achTypes"
            box
            :disabled="selectedToken.id ? true : false"
            hide-details
            required
            :error-messages="fields.ach_type ? '' : 'Required'"
          ></v-select>
        </v-flex>
        <v-flex xs6 sm4 pb-0 offset-sm4 v-if="!selectedToken.id">
          <v-text-field
            label="Re-enter Account Number"
            v-model="fields.ach_account_number_verify"
            append-icon="fa-institution"
            :rules="accountVerifyRules"
            box
            hide-details
            required
            :error-messages="fields.ach_account_number_verify ? '' : 'Required'"
            onpaste="return false;"
            ondrop="return false;"
          ></v-text-field>
        </v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap>
        <v-flex xs12 sm4>
          <v-text-field
            label="Address"
            v-model="fields.billing_address"
            append-icon="fa-home"
            box
            :disabled="billing.id ? true : false"
            hide-details
            required
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <v-text-field
            label="City"
            v-model="fields.billing_city"
            box
            :disabled="billing.id ? true : false"
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <StateField
            v-model="fields.billing_state"
            :textfield="fields.billing_country !== 'USA'"
            :disabled="billing.id ? true : false"
          ></StateField>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            label="Postal Code"
            v-model="fields.billing_postalcode"
            :rules="postalcodeRules"
            box
            :disabled="billing.id ? true : false"
            hide-details
            :error-messages="fields.card_name ? '' : 'Required'"
            required
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            label="Phone"
            v-model="fields.billing_phone"
            mask="(###) ###-####"
            append-icon="fa-phone"
            box
            :disabled="billing.id ? true : false"
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            label="Email"
            v-model="fields.billing_email"
            append-icon="fa-at"
            :rules="emailRules"
            box
            :disabled="billing.id ? true : false"
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <CountryField
            v-model="fields.billing_country"
            icon="fa-globe"
            :disabled="billing.id ? true : false"
          ></CountryField>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            label="Customer PO"
            v-model="fields.customer_po"
            append-icon="fa-file-text-o"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Used to relate transactions to your management software.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap>
        <v-flex xs6 sm4>
          <v-text-field
            label="Custom 1"
            v-model="fields.custom_field_1"
            maxlength="30"
            append-icon="fa-edit"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Any data you put into custom fields 1, 2 or 3 will print on the receipt.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <v-text-field
            label="Custom 2"
            v-model="fields.custom_field_2"
            append-icon="fa-edit"
            maxlength="30"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Any data you put into custom fields 1, 2 or 3 will print on the receipt.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <v-text-field
            label="Custom 3"
            v-model="fields.custom_field_3"
            append-icon="fa-edit"
            maxlength="30"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Any data you put into custom fields 1, 2 or 3 will print on the receipt.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
        <v-flex xs12>
          <v-textarea
            label="Notes (Internal Use Only will not print on customer receipt)"
            v-model="fields.notes"
            box
            hide-details
            height
            rows="1"
          ></v-textarea>
        </v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap align-center fill-height>
          <v-flex xs12 sm4>
          </v-flex>

            <v-flex xs12 sm8>
              <v-layout row wrap justify-end>
                <v-btn color="primary" @click="validateBeforeProcessing" :disabled="!isFormValid || isProcessing || (!accountCanRefund && !fields.override)">process</v-btn>
                <v-btn @click="reset">clear</v-btn>
              </v-layout>
            </v-flex>
      </v-layout>
    </v-form>

    <v-dialog v-model="resultModal" maxWidth="500px" @keydown.esc.prevent="closeResult()" @input="v => v || closeResult()" :transition="false">
      <v-toolbar dense>
        <v-toolbar-title><v-icon v-if="isResponseError">fa-exclamation-triangle</v-icon><v-icon v-if="!isResponseError">fa-check</v-icon> {{ isResponseError ? 'Error' : 'Success' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon flat color="primary" @click="closeResult">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card>
        <v-card-text class="text-xs-center">
          <h3 v-if="isResponseError">{{ responseMessage }}</h3>

          <h4 class="primary--text" v-if="isResponseError && responseMessageDetails">{{ responseMessageDetails }}</h4>

          <h3 v-if="!isResponseError">
            <template v-if="Object.keys(transactionDetails).length && !isNaN(transactionDetails.total_amount)">
              The standalone refund for <span class="title primary--text">${{ (transactionDetails.total_amount / 100).toFixed(2) }}</span> has been <span class="title primary--text">approved</span>.
            </template>

            <div v-if="responseMessageDetails">{{ responseMessageDetails }}.</div>

          </h3>

        </v-card-text>
        <v-card-actions v-if="!isResponseError">
          <v-container fluid grid-list-lg ma-0 pa-0>
            <v-layout wrap justify-center>
              <v-flex xs6 class="text-xs-center" v-if="Object.keys(this.transactionDetails).length">
                <v-btn @click="printReceipt">Print Receipt</v-btn>
              </v-flex>

              <v-flex xs6 class="text-xs-center" v-if="Object.keys(this.transactionDetails).length && fields.billing_email">
                <v-btn @click="emailReceipt">Email Receipt</v-btn>
              </v-flex>

            </v-layout>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmModal
      v-model="showConfirmationModal"
      :amount="fields.amount"
      priceAdjustment=0
      :total="fields.amount"
      @confirmed="process"
      title="standalone refund"
    ></ConfirmModal>

    <ReceiptPrint ref="receipt" :transaction="transactionDetails"></ReceiptPrint>

  </v-container>
</template>

<script>
import Bus from '@/bus'

import APIMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import UtilityMixin from '@/mixins/utility'
import ValidationMixin from '@/mixins/validation'

import AccountTerminalDebug from '@/components/terminal/components/AccountTerminalDebug'
import AchFindInfoModal from '@/components/help/AchFindInfoDialog.vue'
import AmountThreshold from '@/components/terminal/AmountThreshold'
import ConfirmModal from '@/components/ConfirmModal'
import CountryField from '@/components/forms/CountryDropdown'
import Lightbulb from '@/components/help/Lightbulb'
import ReceiptPrint from '@/components/transactions/ReceiptPrint'
import StateField from '@/components/forms/StateDropdown'
import TerminalSelect from '@/components/terminal/components/TerminalSelect'

export default {
  mixins: [
    APIMixin,
    AuthMixin,
    UtilityMixin,
    ValidationMixin
  ],

  components: {
    AccountTerminalDebug,
    AchFindInfoModal,
    AmountThreshold,
    ConfirmModal,
    CountryField,
    Lightbulb,
    ReceiptPrint,
    StateField,
    TerminalSelect
  },

  props: {
    paymentType: String
  },

  data: function () {
    return {
      isFormValid: false,
      selectedTerminal: {},
      billing: {},
      selectedToken: {},
      fields: this.defaultFields(),
      isProcessing: false,
      transactionDetails: {},
      responseMessage: '',
      responseMessageDetails: '',
      isResponseError: false,
      showConfirmationModal: false,
      receiptModal: false,
      resultModal: false,
      submitNonce: '',
      achTypes: [
        {value: 'CHECKING', text: 'Personal checking'},
        {value: 'SAVINGS', text: 'Personal savings'},
        {value: 'BUSINESSCK', text: 'Business checking'}
      ],
      achAuthOptions: [
        {value: 'none', text: 'None'},
        {value: 'onfile', text: 'Authorization on file'},
        {value: 'printsign', text: 'Print and sign now'},
        {value: 'email', text: 'Send authorization email'}
      ],
      // terminalRules: [
      //   (v) => !!v || 'Terminal is required'
      // ],
      nameRules: [
        (v) => !!v || 'Name is required'
      ],
      cardRules: [
        (v) => !!v || 'Card number is required',
        (v) => this.validateCardNumber(v) || 'Card number is invalid'
      ],
      cvvRules: [
        (v) => !!v || 'CVV is required'
      ],
      expirationRules: [
        (v) => !!v || 'Expiration is required'
      ],
      routingRules: [
        (v) => !!v || 'Routing number is required',
        (v) => v.length === 9 || 'Routing number must be nine digits',
        (v) => this.validateABA(v) || 'Routing number is invalid'
      ],
      accountRules: [
        (v) => !!v || 'Account number is required',
        (v) => (v.length < 18) || 'Account number must be less than 18 digits'
        // (v) => (v.toString() === this.fields.ach_account_number_verify) || 'Account number does not match verification'
      ],
      accountVerifyRules: [
        (v) => !!v || 'Account number verification is required',
        (v) => (v.length < 18) || 'Account number verification must be less than 18 digits',
        (v) => (v.toString() === this.fields.ach_account_number) || 'Account number verification does not match'
      ],
      postalcodeRules: [
        (v) => !!v || 'Postal code is required'
      ],
      emailRules: [
        (v) => !v || /.+@.+/.test(v) || 'Email must be valid'
      ]
    }
  },

  mounted () {
    this.getBilling()
  },

  computed: {
    accountCanRefund () {
      return this.accountSelected && this.accountSelected.hasOwnProperty('permissions') && this.accountSelected.permissions && this.accountSelected.permissions.hasOwnProperty('can_standalone_refund') && this.accountSelected.permissions.can_standalone_refund
    },

    cardMask () {
      if (this.selectedToken.id) {
        return null
      }
      if (this.cardType === 'amex') {
        return '#### ###### #####'
      } else if (this.cardType === 'diners') {
        return '#### ###### #####'
      } else if (this.cardType === 'discover') {
        return '#### #### #### ####'
      } else if (this.cardType === 'jcb') {
        return '#### #### #### ####'
      } else if (this.cardType === 'jcb15') {
        return '#### ###### #####'
      } else if (this.cardType === 'maestro') {
        return '#### #### #### ####'
      } else if (this.cardType === 'mastercard') {
        return '#### #### #### ####'
      } else if (this.cardType === 'unionpay') {
        return '#### #### #### ####'
      } else if (this.cardType === 'visa') {
        return '#### #### #### ####'
      }
      return '#### #### #### ####'
    },

    cvvMask () {
      if (this.selectedToken) {
        return null
      }
      if (this.cardType === 'amex' || this.cardType === 'diners' || this.cardType === 'jcb15') {
        return '####'
      }
      return '###'
    },

    cardType () {
      if (this.selectedToken) {
        return this.selectedToken.payment_type
      } else {
        let amex = /^3[47]\d{0,13}/
        let diners = /^3(?:0([0-5]|9)|[689]\d?)\d{0,11}/
        let discover = /^(?:6011|65\d{0,2}|64[4-9]\d?)\d{0,12}/
        let jcb = /^(?:35\d{0,2})\d{0,12}/
        let jcb15 = /^(?:2131|1800)\d{0,11}/
        let maestro = /^(?:5[0678]\d{0,2}|6304|67\d{0,2})\d{0,12}/
        let mastercard = /^(5[1-5]\d{0,2}|22[2-9]\d{0,1}|2[3-7]\d{0,2})\d{0,12}/
        let unionpay = /^62\d{0,14}/
        let visa = /^4\d{0,15}/

        if (amex.test(this.fields.card_number)) {
          return 'amex'
        } else if (diners.test(this.fields.card_number)) {
          return 'diners'
        } else if (discover.test(this.fields.card_number)) {
          return 'discover'
        } else if (jcb.test(this.fields.card_number)) {
          return 'jcb'
        } else if (jcb15.test(this.fields.card_number)) {
          return 'jcb15'
        } else if (maestro.test(this.fields.card_number)) {
          return 'maestro'
        } else if (mastercard.test(this.fields.card_number)) {
          return 'mastercard'
        } else if (unionpay.test(this.fields.card_number)) {
          return 'unionpay'
        } else if (visa.test(this.fields.card_number)) {
          return 'visa'
        }
        return 'unknown'
      }
    }
  },

  methods: {
    defaultFields () {
      return {
        amount: 0.0,
        card_name: '',
        card_number: '',
        card_cvv: '',
        card_expiration: '',
        ach_routing_number: '',
        ach_account_number: '',
        ach_account_number_verify: '',
        ach_type: 'CHECKING',
        ach_authorization: 'none',
        billing_address: '',
        billing_city: '',
        billing_state: '',
        billing_postalcode: '',
        billing_country: 'USA',
        billing_phone: null,
        billing_email: null,
        customer_po: null,
        custom_field_1: null,
        custom_field_2: null,
        custom_field_3: null,
        notes: null,
        override: false
      }
    },

    async reset (opts) {
      this.selectedTerminal = {}
      // this.$refs.terminal.reset()
      this.isProcessing = false
      this.responseMessage = ''
      this.responseMessageDetails = ''
      this.isResponseError = false
      this.transactionDetails = {}
      this.showConfirmationModal = false
      this.resultModal = false
      this.receiptModal = false
      this.$refs.form1.resetValidation()
    },

    async getBilling () {
      if (this.$route.query.billing_id && this.$route.query.token_id) {
        try {
          // @TODO should this be done on every reset?
          let data = await this.api({url: `/billings/${this.$route.query.billing_id}`})

          if (!data.data || !data.data.billing || !data.data.billing.tokens || data.data.error) {
            if (data.data.error) {
              throw new Error(data.data.error)
            } else {
              throw new Error('Invalid token information')
            }
          }

          this.billing = data.data.billing

          this.fields.card_name = this.billing.name
          this.fields.billing_email = this.billing.email
          this.fields.billing_phone = this.billing.phone
          this.fields.billing_address = this.billing.address
          this.fields.billing_city = this.billing.city
          this.fields.billing_state = this.billing.state
          this.fields.billing_postalcode = this.billing.postal_code
          this.fields.billing_country = this.billing.country
          this.fields.customer_po = this.billing.customer_po

          this.selectedToken = this.billing.tokens.find((token) => token.id.toString() === this.$route.query.token_id.toString())

          // Set the payment specific options and terminal
          if (this.selectedToken && this.selectedToken.terminal_id) {
            if (this.paymentType === 'ach') {
              this.selectedTerminal = this.terminalsACHCanCharge.find(terminal => terminal.terminal_id === this.selectedToken.terminal_id)

              this.fields.ach_routing_number = this.selectedToken.ach_aba
              this.fields.ach_account_number = `***** ${this.selectedToken.last_four}`
              this.fields.ach_payment = this.selectedToken.ach_payment_type
              this.fields.ach_type = this.selectedToken.ach_account_type
            } else {
              this.selectedTerminal = this.terminalsCardCanCharge.find(terminal => terminal.terminal_id === this.selectedToken.terminal_id)

              this.fields.card_expiration = this.selectedToken.card_expiration_yymm.slice(-2) + ' / ' + this.selectedToken.card_expiration_yymm.slice(0, 2)
              this.fields.card_cvv = '***'
              this.fields.card_number = `************ ${this.selectedToken.last_four}`
            }

            if (Object.keys(this.selectedTerminal).length > 0 && this.selectedTerminal.hasOwnProperty('account_id') && this.selectedTerminal.account_id) {
              this.$store.commit('SELECT_ACCOUNT', this.selectedTerminal.account_id)
            }
          } else {
            throw new Error('Cannot find a valid terminal')
          }
        } catch (err) {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Problem loading token information',
            messageDetails: err.message
          })
        } finally {
          this.loading = false
        }
      } else if (this.$route.query.token_id) {
        // @TODO Lookup token only
      }
    },

    closeResult () {
      if (!this.isResponseError) {
        this.reset()
      } else {
        this.resultModal = false

        this.isProcessing = false
        this.responseMessage = ''
        this.responseMessageDetails = ''
        this.isResponseError = false
      }
    },

    async validateBeforeProcessing () {
      if (!this.$refs.form1.validate()) {
        let errors = []
        this.$refs.form1.inputs.forEach(e => {
          if (e.hasError) {
            errors.push(e.errorBucket[0])
          }
        })

        let message = 'Validation Error'

        if (errors.length > 1) {
          message = 'Validation Errors'
        }

        Bus.$emit('openModal', {
          status: 'error',
          message: message,
          messageDetails: errors
        })

        return false
      } else {
        this.showConfirmModal()
      }
    },

    showConfirmModal () {
      this.showConfirmationModal = true
    },

    async process () {
      try {
        let options = { ...this.fields }

        if (!this.selectedTerminal.terminal_id) {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Please select a terminal'
          })

          return false
        }

        Bus.$emit('openWaitModal')

        this.isProcessing = true

        options.terminal_id = this.selectedTerminal.terminal_id
        if (this.selectedToken && this.selectedToken.token) {
          options.token = this.selectedToken.token
        }

        if (this.paymentType === 'card') {
          options.card_month = options.card_expiration.slice(0, 2)
          options.card_year = options.card_expiration.slice(-2)
        }

        if (this.paymentType === 'ach') {

        }

        options.request_type = this.paymentType
        options.nonce = this.submitNonce

        let result = await this.api({
          method: 'post',
          url: `/terminal/standaloneRefund`,
          data: options
        })

        // process the refund
        if (result.data.response.ResponseText === 'RETURN ACCEPTED') {
          this.transactionDetails = result.data.response.Transaction
          this.responseMessage = 'Refund Approved'
        } else {
          let error = result.data.response.ResponseText
          if (result.data.response.ErrMsg) {
            error += ': ' + result.data.response.ErrMsg
          }
          this.transactionDetails = {}
          this.isResponseError = true
          this.responseMessage = error
          this.responseMessageDetails = ''
          this.isProcessing = false

          if (error === 'DECLINED') {
            this.responseMessageDetails = 'Please verify card data including the expiration and CVV'
          }

          if (result.data.response.Transaction && result.data.response.Transaction.hasOwnProperty('error_messages') && result.data.response.Transaction.error_messages) {
            let messages = JSON.parse(result.data.response.Transaction.error_messages)

            // show detailed error as long as it isn't the generic 'do not honor'
            if (messages.length > 0 && messages[0] !== 'TRANS DENIED DO NOT HONOR' && messages[0] !== error) {
              this.responseMessageDetails = messages.join()
            }
          }

          this.submitNonce = this.generateId(36)
        }

        this.resultModal = true
        this.panel = [true]
      } catch (err) {
        this.submitNonce = this.generateId(36)
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      } finally {
        this.isProcessing = false
        Bus.$emit('closeWaitModal')
      }
    },

    printReceipt () {
      this.$refs.receipt.print()
    },

    async emailReceipt () {
      try {
        if (!this.transactionDetails || !this.transactionDetails.id) {
          throw new Error('Unable to identify transaction')
        }

        let result = await this.api({
          method: 'get',
          url: `/transactions/${this.transactionDetails.id}/send_receipt_email`
        })

        if (result.data && result.data.error === false) {
          Bus.$emit('openModal', {
            status: 'success',
            message: 'Email Sent'
          })
        } else {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Unexpected Error'
          })
        }
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      }
    }

  }
}
</script>



// WEBPACK FOOTER //
// src/components/manage/StandaloneRefund.vue